import { createFileRoute } from "@tanstack/react-router";
import { ogImageMeta } from "@/lib/seo/og-image";
import { LocaleLink } from "@/components/site/LocaleLink";
import {
  ArrowRight,
  Compass,
  Target,
  Users,
  Filter,
  Rocket,
  Building2,
  Globe2,
  Languages,
  Layers,
  Quote,
} from "lucide-react";
import { SiteHeader } from "@/components/site/SiteHeader";
import { SiteFooter } from "@/components/site/SiteFooter";
import { Button } from "@/components/ui/button";
import { Reveal } from "@/components/site/Reveal";

import { pageSchemas } from "@/lib/seo/page-schema";
const ABOUT_DESCRIPTION =
  "Meet Perceptica, the team behind media monitoring in SEE. Independent analysts, full regional coverage and transparent media intelligence methodology.";

const pillars = [
  {
    icon: Compass,
    title: "Full-spectrum intelligence",
    body: "From real-time monitoring to in-depth reports and consulting, across broadcast, print, online and social.",
  },
  {
    icon: Target,
    title: "Reputation & competition",
    body: "Understand the reputation of your brand and analyse the actions of your competitors with the same lens.",
  },
  {
    icon: Users,
    title: "Analysts + technology",
    body: "Our analysts work alongside the latest media listening platforms - neither one alone is enough.",
  },
  {
    icon: Filter,
    title: "Insights, not noise",
    body: "We filter, analyse and synthesise media content into a small number of decisions you can actually act on.",
  },
];

const milestones = [
  {
    year: "2012",
    title: "Perceptica is founded",
    body: "We start with one purpose: provide qualified media analysis to organisations across Southeast Europe.",
  },
  {
    year: "2019",
    title: "Merger with Media Monitor",
    body: "We merge with media monitoring company Media Monitor, accelerating the development of our media listening platform.",
  },
  {
    year: "2021",
    title: "Platform refresh",
    body: "A round of platform updates focused on user experience and making information easier to understand.",
  },
  {
    year: "2022",
    title: "All-in-one social view",
    body: "We cement our position as an all-in-one tool that lets teams view, research and monitor every social platform from a single dashboard.",
  },
  {
    year: "2026",
    title: "Next-generation platform launch",
    body: "We launch our new platform - rebuilt around real-time intelligence, AI-assisted analysis and reporting designed for leadership review, not just dashboards.",
    highlight: true,
  },
];

const memberships = ["AMEC", "Updata One", "FIBEP", "БДВО", "KIDA"];

export const Route = createFileRoute("/about-us")({
  head: () => ({
    meta: [
      { title: "About Perceptica - Media Monitoring in Southeast Europe" },
      { name: "description", content: ABOUT_DESCRIPTION },
      { property: "og:title", content: "About Perceptica - Media Monitoring in Southeast Europe" },
      { property: "og:description", content: ABOUT_DESCRIPTION },
      { property: "og:type", content: "website" },
      ...ogImageMeta("en", "/about-us", "About Perceptica"),
      { name: "twitter:card", content: "summary_large_image" },
      { name: "twitter:title", content: "About Perceptica - Media Monitoring in Southeast Europe" },
      { name: "twitter:description", content: ABOUT_DESCRIPTION },
      { property: "og:url", content: "https://perceptica.com/about-us" },
    ],
  
    scripts: pageSchemas({ locale: "en", path: "/about-us" }),
  }),
  component: AboutPage,
});

function AboutPage() {
  return (
    <div className="min-h-screen bg-background">
      <SiteHeader />
      <main id="main">
        {/* Hero */}
        <section className="relative overflow-hidden bg-primary text-primary-foreground">
          <div className="grid-overlay" />
          <div
            aria-hidden
            className="pointer-events-none absolute -bottom-24 left-0 h-[420px] w-[420px] rounded-full opacity-30 blur-[140px]"
            style={{ background: "radial-gradient(circle, #FF2D78 0%, transparent 70%)" }}
          />
          <div
            aria-hidden
            className="pointer-events-none absolute -top-32 right-0 h-[520px] w-[520px] rounded-full opacity-20 blur-[160px]"
            style={{ background: "radial-gradient(circle, #6B5BFF 0%, transparent 70%)" }}
          />
          <div className="noise-overlay" />
          <div className="relative mx-auto grid max-w-[1200px] gap-14 px-6 py-24 lg:grid-cols-[1.35fr_1fr] lg:items-end lg:gap-16 lg:px-10 lg:py-32">
            <div>
              <span className="inline-flex items-center gap-2 rounded-full border border-white/15 bg-white/5 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.18em] text-white/75">
                Since 2012
              </span>
              <h1 className="font-display mt-6 max-w-[22ch] text-[clamp(2.25rem,4.6vw,3.75rem)] font-medium leading-[1.02] text-white">
                Meet Perceptica
              </h1>
              <p className="mt-7 max-w-xl text-[16px] leading-relaxed text-white/75">
                We are a team of analysts and engineers who have been watching, scoring and explaining the media landscape of Southeast Europe since 2012. Every day we read, code and interpret coverage in more than twenty languages so PR, marketing and leadership teams can make confident calls - on reputation, on competitors, on what comes next.
              </p>
              <div className="mt-10 flex flex-wrap items-center gap-4">
                <Button asChild size="lg">
                  <LocaleLink to="/request-a-demo">Request a demo</LocaleLink>
                </Button>
                <a
                  href="#our-story"
                  className="inline-flex items-center gap-1.5 text-[13.5px] font-semibold text-white/80 transition-colors hover:text-white"
                >
                  Read the Perceptica story
                  <ArrowRight className="h-4 w-4 rotate-90" />
                </a>
              </div>
            </div>
            <aside className="relative">
              <div
                aria-hidden
                className="absolute -inset-6 rounded-[32px] opacity-30 blur-2xl"
                style={{ background: "linear-gradient(135deg,#FF2D78 0%,#6B5BFF 100%)" }}
              />
              <div className="relative rounded-[24px] border border-white/15 bg-white/[0.04] p-7 backdrop-blur-sm">
                <p className="text-[10.5px] font-semibold uppercase tracking-[0.22em] text-white/80">
                  By the numbers
                </p>
                <dl className="mt-5 grid grid-cols-2 gap-y-6 gap-x-5">
                  {[
                    { k: "14+", v: "Years in the market" },
                    { k: "25+", v: "Languages scored natively" },
                    { k: "5", v: "Industry memberships" },
                    { k: "SEE", v: "Regional coverage" },
                  ].map((s) => (
                    <div key={s.v}>
                      <dt className="font-display text-[1.85rem] font-medium leading-none text-white">
                        {s.k}
                      </dt>
                      <dd className="mt-2 text-[12.5px] leading-snug text-white/65">{s.v}</dd>
                    </div>
                  ))}
                </dl>
              </div>
            </aside>
          </div>
        </section>

        {/* Principles */}
        <section className="border-b border-border/60 bg-background py-20 lg:py-28">
          <div className="mx-auto max-w-[1100px] px-6 lg:px-10">
            <Reveal className="mb-12 max-w-2xl">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-accent">
                How we work
              </p>
              <h2 className="font-display mt-3 text-[clamp(1.75rem,2.6vw,2.25rem)] text-foreground">
                Four principles we have refined over a decade.
              </h2>
            </Reveal>
            <ol className="grid gap-px overflow-hidden rounded-2xl border border-border/60 bg-border/60 sm:grid-cols-2">
              {pillars.map((p, i) => (
                <Reveal
                  key={p.title}
                  className="flex flex-col gap-4 bg-background p-8 lg:p-10"
                >
                  <div className="flex items-center gap-4">
                    <span className="font-display text-[12px] font-semibold tracking-[0.22em] text-accent">
                      {String(i + 1).padStart(2, "0")}
                    </span>
                    <span className="h-px flex-1 bg-border/60" />
                    <span className="inline-flex h-9 w-9 items-center justify-center rounded-[10px] bg-accent/10 text-accent">
                      <p.icon className="h-4.5 w-4.5" />
                    </span>
                  </div>
                  <h3 className="font-display text-[1.25rem] leading-snug text-foreground">
                    {p.title}
                  </h3>
                  <p className="text-[14.5px] leading-relaxed text-foreground/75">
                    {p.body}
                  </p>
                </Reveal>
              ))}
            </ol>
          </div>
        </section>

        {/* Story / Milestones */}
        <section id="our-story" className="scroll-mt-24 py-20 lg:py-28">
          <div className="mx-auto max-w-[1100px] px-6 lg:px-10">
            <Reveal className="mb-12 max-w-2xl">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-accent">
                Our story
              </p>
              <h2 className="font-display mt-3 text-[clamp(1.75rem,2.6vw,2.25rem)] text-foreground">
                From a regional analysis team to a full-spectrum intelligence platform.
              </h2>
              <p className="mt-4 text-[14.5px] leading-relaxed text-muted-foreground">
                Each step here was driven by what our clients needed next - faster signal, deeper context, broader coverage. Here is how the work has compounded.
              </p>
            </Reveal>
            <ol className="relative ml-3 border-l border-border/70 pl-8 sm:ml-6 sm:pl-12">
              {milestones.map((m) => (
                <Reveal
                  key={m.year}
                  className="relative pb-12 last:pb-0"
                >
                  <span
                    aria-hidden
                    className={`absolute -left-[34px] top-1.5 h-3.5 w-3.5 rounded-full ring-4 sm:-left-[58px] ${
                      m.highlight
                        ? "bg-accent ring-accent/15"
                        : "bg-foreground/30 ring-background"
                    }`}
                  />
                  <div
                    className={`rounded-2xl border bg-background p-6 lg:p-7 ${
                      m.highlight
                        ? "border-accent/40 shadow-[var(--shadow-soft)]"
                        : "border-border/60"
                    }`}
                  >
                    <div className="flex flex-wrap items-center gap-3">
                      <span className="font-display text-[12px] font-semibold uppercase tracking-[0.22em] text-accent">
                        {m.year}
                      </span>
                      <span className="h-px flex-1 bg-border/60" />
                      {m.highlight && (
                        <span className="inline-flex items-center gap-1.5 rounded-full border border-accent/30 bg-accent/10 px-2.5 py-0.5 text-[10.5px] font-semibold uppercase tracking-[0.16em] text-accent">
                          <Rocket className="h-3 w-3" /> New
                        </span>
                      )}
                    </div>
                    <h3 className="font-display mt-4 text-[1.2rem] leading-snug text-foreground">
                      {m.title}
                    </h3>
                    <p className="mt-3 text-[14.5px] leading-relaxed text-foreground/80">
                      {m.body}
                    </p>
                  </div>
                </Reveal>
              ))}
            </ol>
          </div>
        </section>

        {/* Mission - pull quote */}
        <section className="relative overflow-hidden bg-secondary/40 py-24 lg:py-32">
          <div
            aria-hidden
            className="pointer-events-none absolute -left-32 top-1/2 h-[420px] w-[420px] -translate-y-1/2 rounded-full opacity-20 blur-[140px]"
            style={{ background: "radial-gradient(circle,#FF2D78 0%,transparent 70%)" }}
          />
          <div className="relative mx-auto max-w-[1100px] px-6 lg:px-10">
            <Reveal className="relative max-w-3xl border-l-2 border-accent pl-8 lg:pl-12">
              <Quote className="absolute -left-3 -top-2 h-6 w-6 -translate-x-1/2 rotate-180 bg-secondary/40 text-accent" />
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-accent">
                Mission
              </p>
              <p className="font-display mt-5 text-[clamp(1.6rem,2.6vw,2.25rem)] font-medium leading-[1.2] text-foreground">
                "Every decision-maker should have access to qualified information. Period."
              </p>
              <div className="mt-7 space-y-4 text-[15px] leading-relaxed text-foreground/80">
                <p>
                  Perceptica's primary mission is to help businesses make the best possible decisions, optimise their communication strategies, and track market developments.
                </p>
                <p>
                  We give our clients the information they need by filtering, analysing and synthesising media content into a small number of decisive insights - not raw data dumps.
                </p>
              </div>
              <p className="mt-8 text-[12.5px] font-semibold uppercase tracking-[0.18em] text-muted-foreground">
                - The Perceptica team, est. 2012
              </p>
            </Reveal>
          </div>
        </section>

        {/* Proof strip */}
        <section className="border-y border-border/60 bg-background py-16">
          <div className="mx-auto max-w-[1100px] px-6 lg:px-10">
            <div className="grid gap-10 sm:grid-cols-3">
              {[
                { icon: Globe2, k: "Southeast Europe", v: "Native coverage of every major market in the region, from Sofia to Skopje to Bucharest." },
                { icon: Languages, k: "25+ languages", v: "Sentiment and topic models trained in-language - no English-pivot translation tax." },
                { icon: Layers, k: "Industries served", v: "Pharma, finance, FMCG, retail, automotive, energy, technology and the public sector." },
              ].map((s) => (
                <Reveal key={s.k} className="flex flex-col gap-3">
                  <span className="inline-flex h-10 w-10 items-center justify-center rounded-[10px] bg-accent/10 text-accent">
                    <s.icon className="h-5 w-5" />
                  </span>
                  <p className="font-display text-[1.05rem] text-foreground">{s.k}</p>
                  <p className="text-[13.5px] leading-relaxed text-muted-foreground">{s.v}</p>
                </Reveal>
              ))}
            </div>
            <div className="mt-10 flex flex-wrap items-center gap-x-6 gap-y-3 text-[13px] font-semibold">
              <LocaleLink to="/reports" className="inline-flex items-center gap-1 text-accent hover:underline">
                See the analyses we publish
                <ArrowRight className="h-3.5 w-3.5" />
              </LocaleLink>
              <LocaleLink to="/why-perceptica" className="inline-flex items-center gap-1 text-accent hover:underline">
                Why teams choose us
                <ArrowRight className="h-3.5 w-3.5" />
              </LocaleLink>
            </div>
          </div>
        </section>

        {/* Memberships */}
        <section className="py-20 lg:py-28">
          <div className="mx-auto max-w-[1100px] px-6 lg:px-10">
            <Reveal className="mb-10 max-w-2xl text-center sm:mx-auto">
              <p className="text-[11px] font-semibold uppercase tracking-[0.22em] text-accent">
                Memberships
              </p>
              <h2 className="font-display mt-3 text-[clamp(1.75rem,2.6vw,2.25rem)] text-foreground">
                Standards we help shape and adhere to.
              </h2>
              <p className="mt-4 text-[14.5px] leading-relaxed text-muted-foreground">
                Perceptica is a member of organisations that set the standards for media intelligence and communications measurement.
              </p>
            </Reveal>
            <div className="flex flex-wrap items-center justify-center gap-3">
              {memberships.map((m) => (
                <span
                  key={m}
                  className="inline-flex items-center gap-2 rounded-full border border-border/60 bg-background px-5 py-2.5 text-[14px] font-semibold text-foreground/85 shadow-[var(--shadow-soft)] transition-transform hover:-translate-y-0.5 hover:border-accent/40"
                >
                  <Building2 className="h-4 w-4 text-accent" />
                  {m}
                </span>
              ))}
            </div>
          </div>
        </section>

        {/* CTA */}
        <section className="bg-primary text-primary-foreground">
          <div className="mx-auto flex max-w-[1100px] flex-col items-center gap-6 px-6 py-20 text-center lg:py-24">
            <h2 className="font-display max-w-[28ch] text-[clamp(1.75rem,3vw,2.5rem)] text-white">
              Want the same lens on your market?
            </h2>
            <p className="max-w-2xl text-[15px] leading-relaxed text-white/70">
              Book a demo of the new Perceptica platform, or talk to one of the analysts who will actually be reading your coverage.
            </p>
            <div className="flex flex-wrap items-center justify-center gap-4">
              <Button asChild size="xl">
                <LocaleLink to="/request-a-demo">
                  Request a demo
                  <ArrowRight className="h-4 w-4" />
                </LocaleLink>
              </Button>
              <Button asChild size="xl" variant="outline" className="border-white/30 bg-transparent text-white hover:bg-white/10">
                <LocaleLink to="/contact-us">Talk to an analyst</LocaleLink>
              </Button>
            </div>
          </div>
        </section>
      </main>
      <SiteFooter />
    </div>
  );
}
